import React, { useState, useEffect } from 'react';
import { Pencil, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Resource } from '../../lib/resources';
import { getResourceRoles, ResourceRole, assignRoleToResource } from '../../lib/resourceRoles';
import { useBusiness } from '../../contexts/BusinessContext';

interface PermissionRoleSectionProps {
  resource: Resource;
  editMode: string | null;
  onEdit: (section: string) => void;
  onUpdate: () => void;
}

export default function PermissionRoleSection({
  resource,
  editMode,
  onEdit,
  onUpdate
}: PermissionRoleSectionProps) {
  const { selectedBusiness } = useBusiness();
  const [permissionRoles, setPermissionRoles] = useState<ResourceRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>(resource.role_id || '');
  const navigate = useNavigate();

  const loadPermissionRoles = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const roles = await getResourceRoles(selectedBusiness.id);
      setPermissionRoles(roles);
    } catch (err) {
      console.error('Error loading permission roles:', err);
    }
  };

  useEffect(() => {
    if (editMode === 'permissions') {
      loadPermissionRoles();
      setSelectedRoleId(resource.role_id || '');
    }
  }, [editMode, resource.role_id]);

  const handleSave = async () => {
    try {
      setLoading(true);
      await assignRoleToResource(resource.id, selectedRoleId || null);
      onUpdate();
      onEdit(''); // Exit edit mode
    } catch (err) {
      console.error('Error assigning role:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedRoleId(resource.role_id || '');
    onEdit('');
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-medium text-gray-900">Permission Role</h3>
          <button
            onClick={() => navigate('/settings', { state: { activeTab: 'resource-permissions' } })}
            className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 hover:text-gray-700 transition-colors"
          >
            <Settings className="h-3 w-3 mr-1" />
            Configure Roles
          </button>
        </div>
        {editMode === 'permissions' ? (
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCancel}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <button
            onClick={() => onEdit('permissions')}
            className="text-gray-400 hover:text-gray-600"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="p-4">
        {editMode === 'permissions' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Assign Permission Role
              </label>
              <select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                className="w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
              >
                <option value="">No specific permissions</option>
                {permissionRoles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Select a permission role to control what this team member can access
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="text-xs text-gray-500">Current Permission Role</div>
              <div className="text-sm text-gray-900 mt-1">
                {resource.permission_role?.name || (
                  <span className="text-gray-500 italic">No permissions assigned</span>
                )}
              </div>
            </div>
            {resource.permission_role?.description && (
              <div>
                <div className="text-xs text-gray-500">Description</div>
                <div className="text-sm text-gray-900 mt-1">
                  {resource.permission_role.description}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 